/**
 * Reports Charts Module
 *
 * Reads chart configuration from a <script type="application/json" id="report-chart-data">
 * block injected by Django's json_script filter, then renders ApexCharts instances.
 *
 * Each tab (revenue, billing, operations, workforce, customerDetail) has its own
 * set of chart renderers registered in RENDERERS. Adding a new chart only requires
 * adding the data key in the Django view and a renderer entry here.
 */

// ---------------------------------------------------------------------------
// Theme helpers
// ---------------------------------------------------------------------------

function getTheme() {
    var isDark = document.documentElement.classList.contains('dark');
    return {
        isDark: isDark,
        fg: isDark ? '#d1d5db' : '#374151',
        grid: isDark ? '#374151' : '#e5e7eb',
        primary: '#1C64F2',
    };
}

function noDataHtml(label) {
    return '<p class="text-sm text-gray-400 text-center py-16">' + label + '</p>';
}

function currencyFormatter(v) {
    return '\u20AC' + v.toLocaleString();
}

// ---------------------------------------------------------------------------
// Generic render helpers
// ---------------------------------------------------------------------------

function renderTrendChart(el, cfg, theme, labels) {
    el.innerHTML = '';
    var values = cfg.values;
    if (!values.length) {
        el.innerHTML = noDataHtml(labels.noData);
        return null;
    }
    var chartType = cfg.type || 'area';
    if (values.length <= 2) chartType = 'bar';
    var isBar = chartType === 'bar';
    var opts = {
        chart: { type: chartType, height: 256, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
        series: [{ name: labels.revenue || labels.seriesName || '', data: values }],
        xaxis: { categories: cfg.labels, labels: { style: { colors: theme.fg, fontSize: '11px' }, rotate: isBar ? -45 : 0, rotateAlways: isBar } },
        yaxis: { min: 0, labels: { style: { colors: theme.fg, fontSize: '11px' }, formatter: currencyFormatter } },
        colors: [theme.primary],
        grid: { borderColor: theme.grid },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: currencyFormatter } },
        noData: { text: labels.noData, style: { color: theme.fg, fontSize: '14px' } },
    };
    if (isBar) {
        opts.plotOptions = { bar: { borderRadius: 4, columnWidth: '60%' } };
    } else {
        opts.fill = { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } };
        opts.stroke = { width: 2, curve: 'smooth' };
    }
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

function renderDonut(el, cfg, theme, labels, colors) {
    el.innerHTML = '';
    if (!cfg.values || !cfg.values.length) {
        el.innerHTML = noDataHtml(labels.noData);
        return null;
    }
    var formatter = cfg.formatter === 'currency' ? currencyFormatter : undefined;
    var opts = {
        chart: { type: 'donut', height: 256, fontFamily: 'Inter, sans-serif' },
        series: cfg.values,
        labels: cfg.labels,
        colors: colors,
        legend: { position: 'bottom', labels: { colors: theme.fg } },
        dataLabels: { enabled: true, style: { fontSize: '11px' } },
    };
    if (formatter) {
        opts.tooltip = { y: { formatter: formatter } };
    }
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

function renderDonutPair(el, cfg, theme, labels, colors) {
    el.innerHTML = '';
    if (!cfg.valueA && !cfg.valueB) {
        el.innerHTML = noDataHtml(labels.noData);
        return null;
    }
    var opts = {
        chart: { type: 'donut', height: 256, fontFamily: 'Inter, sans-serif' },
        series: [cfg.valueA, cfg.valueB],
        labels: [cfg.labelA, cfg.labelB],
        colors: colors,
        legend: { position: 'bottom', labels: { colors: theme.fg } },
        dataLabels: { enabled: true, style: { fontSize: '12px' } },
    };
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

// ---------------------------------------------------------------------------
// Revenue renderers
// ---------------------------------------------------------------------------

function renderRevenueCustomer(el, cfg, theme, labels) {
    el.innerHTML = '';
    var opts = {
        chart: { type: 'bar', height: 256, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
        series: [{ name: labels.revenue, data: cfg.values }],
        xaxis: { categories: cfg.labels, labels: { style: { colors: theme.fg, fontSize: '10px' }, trim: true, maxHeight: 80 } },
        yaxis: { labels: { style: { colors: theme.fg, fontSize: '11px' }, formatter: currencyFormatter } },
        plotOptions: { bar: { borderRadius: 4, horizontal: false } },
        colors: [theme.primary],
        grid: { borderColor: theme.grid },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: currencyFormatter } },
    };
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

// Horizontal bar chart for the subcontracting axes (PR 3.2). Horizontal
// orientation is deliberate: end-client and work-site labels are long
// (e.g. ``« Galerie Wittamer — Place du Grand Sablon 12, 1000 Bruxelles »``)
// and would be unreadable as truncated vertical x-axis labels. The empty-
// state guard matters here because the chart is only registered when the
// tenant has ``subcontracting_enabled = True`` but may still legitimately
// have ZERO subcontracting lines in the selected period.
function renderRevenueHorizontalBar(el, cfg, theme, labels, color) {
    el.innerHTML = '';
    if (!cfg.values || !cfg.values.length) {
        el.innerHTML = noDataHtml(labels.noData);
        return null;
    }
    var opts = {
        chart: { type: 'bar', height: 256, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
        series: [{ name: labels.revenue, data: cfg.values }],
        xaxis: { labels: { style: { colors: theme.fg, fontSize: '11px' }, formatter: currencyFormatter } },
        yaxis: { categories: cfg.labels, labels: { style: { colors: theme.fg, fontSize: '11px' }, maxWidth: 220 } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        colors: [color || theme.primary],
        grid: { borderColor: theme.grid },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: currencyFormatter } },
    };
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

// ---------------------------------------------------------------------------
// Billing renderers
// ---------------------------------------------------------------------------

function renderAgingBar(el, cfg, theme, labels) {
    el.innerHTML = '';
    var opts = {
        chart: { type: 'bar', height: 256, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
        series: [{ name: labels.amount, data: cfg.values }],
        xaxis: { categories: cfg.labels, labels: { style: { colors: theme.fg, fontSize: '11px' } } },
        yaxis: { labels: { style: { colors: theme.fg, fontSize: '11px' }, formatter: currencyFormatter } },
        colors: ['#31C48D', '#FDBA8C', '#F98080', '#E74694', '#9B1C1C'],
        plotOptions: { bar: { borderRadius: 4, distributed: true } },
        grid: { borderColor: theme.grid },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: { y: { formatter: currencyFormatter } },
    };
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

function renderCreditNotesBar(el, cfg, theme, labels) {
    el.innerHTML = '';
    if (!cfg.values || !cfg.values.length) {
        el.innerHTML = noDataHtml(labels.noCreditNotes);
        return null;
    }
    var opts = {
        chart: { type: 'bar', height: 256, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
        series: [{ name: labels.creditNotes, data: cfg.values }],
        xaxis: { categories: cfg.labels, labels: { style: { colors: theme.fg, fontSize: '11px' } } },
        yaxis: { labels: { style: { colors: theme.fg, fontSize: '11px' }, formatter: currencyFormatter } },
        colors: ['#E74694'],
        plotOptions: { bar: { borderRadius: 4 } },
        grid: { borderColor: theme.grid },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: currencyFormatter } },
    };
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

// ---------------------------------------------------------------------------
// Operations renderers
// ---------------------------------------------------------------------------

function renderGroupedBar(el, cfg, theme, labels) {
    el.innerHTML = '';
    if (!cfg.labels || !cfg.labels.length) {
        el.innerHTML = noDataHtml(labels.noData);
        return null;
    }
    var opts = {
        chart: { type: 'bar', height: 256, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
        series: [
            { name: cfg.seriesNames[0], data: cfg.series[0] },
            { name: cfg.seriesNames[1], data: cfg.series[1] },
        ],
        xaxis: { categories: cfg.labels, labels: { style: { colors: theme.fg, fontSize: '11px' } } },
        yaxis: { labels: { style: { colors: theme.fg, fontSize: '11px' }, formatter: function(v) { return v + 'h'; } } },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        colors: ['#1C64F2', '#16BDCA'],
        grid: { borderColor: theme.grid },
        dataLabels: { enabled: false },
    };
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

function renderOntimeArea(el, cfg, theme, labels) {
    el.innerHTML = '';
    if (!cfg.labels || !cfg.labels.length) {
        el.innerHTML = noDataHtml(labels.noData);
        return null;
    }
    var opts = {
        chart: { type: 'area', height: 256, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
        series: [{ name: labels.ontimePct, data: cfg.values }],
        xaxis: { categories: cfg.labels, labels: { style: { colors: theme.fg, fontSize: '11px' } } },
        yaxis: { min: 0, max: 100, labels: { style: { colors: theme.fg, fontSize: '11px' }, formatter: function(v) { return v + '%'; } } },
        colors: ['#31C48D'],
        fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
        stroke: { width: 2, curve: 'smooth' },
        grid: { borderColor: theme.grid },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: function(v) { return v + '%'; } } },
    };
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

// ---------------------------------------------------------------------------
// Workforce renderers
// ---------------------------------------------------------------------------

function renderHorizontalBar(el, cfg, theme, labels) {
    el.innerHTML = '';
    if (!cfg.labels || !cfg.labels.length) {
        el.innerHTML = noDataHtml(labels.noData);
        return null;
    }
    var opts = {
        chart: { type: 'bar', height: 256, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
        series: [{ name: labels.utilization, data: cfg.values }],
        xaxis: { categories: cfg.labels, labels: { style: { colors: theme.fg, fontSize: '10px' }, trim: true, maxHeight: 80 } },
        yaxis: { min: 0, max: 100, labels: { style: { colors: theme.fg, fontSize: '11px' }, formatter: function(v) { return v + '%'; } } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        colors: ['#1C64F2'],
        grid: { borderColor: theme.grid },
        dataLabels: { enabled: true, formatter: function(v) { return v + '%'; }, style: { fontSize: '11px' } },
    };
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

function renderHoursBar(el, cfg, theme, labels) {
    el.innerHTML = '';
    if (!cfg.labels || !cfg.labels.length) {
        el.innerHTML = noDataHtml(labels.noData);
        return null;
    }
    var opts = {
        chart: { type: 'bar', height: 256, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
        series: [{ name: labels.hours, data: cfg.values }],
        xaxis: { categories: cfg.labels, labels: { style: { colors: theme.fg, fontSize: '10px' }, trim: true, maxHeight: 80 } },
        yaxis: { labels: { style: { colors: theme.fg, fontSize: '11px' }, formatter: function(v) { return v + 'h'; } } },
        plotOptions: { bar: { borderRadius: 4 } },
        colors: ['#16BDCA'],
        grid: { borderColor: theme.grid },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: function(v) { return v + 'h'; } } },
    };
    var chart = new ApexCharts(el, opts);
    chart.render();
    return chart;
}

// ---------------------------------------------------------------------------
// Tab → chart dispatcher
// ---------------------------------------------------------------------------

var DONUT_COLORS_MODE = ['#1C64F2', '#16BDCA', '#9061F9'];
// Palette for the « By Category » donut — a 6-step warm-cool ramp.
var DONUT_COLORS_CATEGORY = ['#7C3AED', '#0EA5E9', '#F59E0B', '#10B981', '#EF4444', '#64748B'];
var DONUT_COLORS_STATUS = ['#1C64F2', '#16BDCA', '#9061F9', '#FDBA8C', '#31C48D', '#F59E0B', '#EF4444'];
var DONUT_COLORS_PUNCTUALITY = ['#31C48D', '#F59E0B'];
var DONUT_COLORS_CANCEL = ['#EF4444', '#F59E0B', '#9061F9', '#1C64F2', '#16BDCA', '#E74694'];
var DONUT_COLORS_ABSENCE = ['#EF4444', '#F59E0B', '#9061F9', '#1C64F2', '#16BDCA', '#E74694', '#6B7280'];
var DONUT_COLORS_CIAO = ['#31C48D', '#F59E0B'];

function renderTab(data, theme) {
    var tab = data.tab;
    var charts = data.charts;
    var labels = data.labels;
    var instances = [];

    function push(inst) { if (inst) instances.push(inst); }
    function el(id) { return document.getElementById(id); }

    if (tab === 'revenue') {
        push(renderTrendChart(el('chart-revenue-trend'), charts.trend, theme, labels));
        push(renderRevenueCustomer(el('chart-revenue-customer'), charts.customer, theme, labels));
        push(renderDonut(el('chart-revenue-mode'), charts.mode, theme, labels, DONUT_COLORS_MODE));
        // PR-6c-4 (2026-05) — « Revenue by Category » donut. Renderer-
        // side guard mirrors the data shape (always emitted by
        // ``_revenue_context``, but defensive against future tabs
        // that may not include it).
        if (charts.category && el('chart-revenue-category')) {
            push(renderDonut(el('chart-revenue-category'), charts.category, theme, labels, DONUT_COLORS_CATEGORY));
        }
        // Subcontracting axes (PR 3.2). Renderer-side guard mirrors the
        // template-side guard : the view only emits these chart_data keys
        // when ``org.subcontracting_enabled`` is true, AND the matching
        // <div> only renders inside the same {% if %} block. Either guard
        // alone is enough, both together make it boringly safe.
        if (charts.endClient && el('chart-revenue-end-client')) {
            push(renderRevenueHorizontalBar(el('chart-revenue-end-client'), charts.endClient, theme, labels, '#9061F9'));
        }
        if (charts.workSite && el('chart-revenue-work-site')) {
            push(renderRevenueHorizontalBar(el('chart-revenue-work-site'), charts.workSite, theme, labels, '#16BDCA'));
        }
    } else if (tab === 'billing') {
        push(renderAgingBar(el('chart-aging'), charts.aging, theme, labels));
        push(renderCreditNotesBar(el('chart-credit-notes'), charts.creditNotes, theme, labels));
    } else if (tab === 'operations') {
        push(renderDonut(el('chart-visit-status'), charts.visitStatus, theme, labels, DONUT_COLORS_STATUS));
        push(renderDonutPair(el('chart-punctuality'), charts.punctuality, theme, labels, DONUT_COLORS_PUNCTUALITY));
        push(renderGroupedBar(el('chart-pva'), charts.pva, theme, labels));
        push(renderDonut(el('chart-cancel'), charts.cancel, theme, labels, DONUT_COLORS_CANCEL));
        push(renderOntimeArea(el('chart-ontime'), charts.ontime, theme, labels));
    } else if (tab === 'workforce') {
        push(renderHorizontalBar(el('chart-utilization'), charts.utilization, theme, labels));
        push(renderHoursBar(el('chart-hours'), charts.hours, theme, labels));
        push(renderDonut(el('chart-absences'), charts.absences, theme, labels, DONUT_COLORS_ABSENCE));
        push(renderDonutPair(el('chart-ciao'), charts.ciao, theme, labels, DONUT_COLORS_CIAO));
    } else if (tab === 'customerDetail') {
        push(renderTrendChart(el('chart-customer-trend'), charts.trend, theme, labels));
    }

    return instances;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

var _chartInstances = [];
var _renderedEl = null;
var _rerenderHandler = null;

export function initReports() {
    window.initReports = initReports;
    var dataEl = document.getElementById('report-chart-data');
    if (!dataEl) return;

    if (_renderedEl === dataEl) return;
    _renderedEl = dataEl;

    var data;
    try {
        data = JSON.parse(dataEl.textContent);
    } catch (e) {
        return;
    }

    var theme = getTheme();

    function doRender() {
        _chartInstances.forEach(function(c) { try { c.destroy(); } catch(e) {} });
        _chartInstances = renderTab(data, theme);
    }

    requestAnimationFrame(function() { setTimeout(doRender, 0); });

    if (_rerenderHandler) {
        document.removeEventListener('rerender-charts', _rerenderHandler);
    }
    _rerenderHandler = function() {
        theme = getTheme();
        requestAnimationFrame(function() { setTimeout(doRender, 0); });
    };
    document.addEventListener('rerender-charts', _rerenderHandler);
}
